import api from '@/api/base';
import type { ApiResponse, PortOfficeModel } from '@/api/types';

// API URLs for utility endpoints
const URLS = {
  portOffices: '/port_office',
  news: '/news',
  newsById: (id: string) => `/news/${id}`,
  upload: '/upload',
  download: (id: string) => `/download/${id}`,
  search: '/search',
  tnved: '/tnved',
  exchange: '/exchange',
  callMeLater: '/callMeLater',
} as const;

type UtilityPayload = Record<string, unknown>;

// Utility API methods
const utilityApi = {
  // Get port offices
  getPortOffices: (token?: string) => {
    return api.get<ApiResponse<PortOfficeModel[]>>(
      URLS.portOffices,
      token ? { token } : {},
    );
  },

  // Get news with pagination. `/news` returns the standard { status, data }
  // envelope; useApiQuery unwraps the axios response once, so consumers read
  // `.data` on the envelope.
  getNews: ({
    page = 1,
    limit = 10,
  }: { page?: number; limit?: number } = {}) => {
    return api.get<ApiResponse<unknown>>(URLS.news, {
      params: { page, limit },
    });
  },

  // Get news by ID
  getNewsById: (id: string) => {
    return api.get<ApiResponse<unknown>>(URLS.newsById(id));
  },

  // Create news article
  createNews: (newsData: UtilityPayload, token?: string) => {
    return api.post<ApiResponse<unknown>>(URLS.news, newsData, { token });
  },

  // Update news article
  updateNews: (id: string, newsData: UtilityPayload, token?: string) => {
    return api.patch<ApiResponse<unknown>>(URLS.newsById(id), newsData, {
      token,
    });
  },

  // Delete news article
  deleteNews: (id: string, token?: string) => {
    return api.delete<ApiResponse<null>>(URLS.newsById(id), { token });
  },

  // Upload file
  uploadFile: (formData: FormData, token?: string) => {
    return api.post<ApiResponse<unknown>>(URLS.upload, formData, {
      token,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Download file
  downloadFile: (id: string, token?: string) => {
    return api.get<Blob>(URLS.download(id), {
      token,
      responseType: 'blob',
    });
  },

  // Search functionality
  search: (query: string, filters: UtilityPayload = {}, token?: string) => {
    return api.get<ApiResponse<unknown>>(URLS.search, {
      token,
      params: { q: query, ...filters },
    });
  },

  // Get TNVED codes (proxied customs lookup; shape not modeled — unknown body).
  getTnvedCodes: (query = '') => {
    return api.get<unknown>(URLS.tnved, {
      params: { q: query },
    });
  },

  // Get exchange rates (proxied currency feed; shape not modeled — unknown body).
  getExchangeRates: () => {
    return api.get<unknown>(URLS.exchange);
  },

  // Submit call me later request (contact form)
  submitCallMeLater: (requestData: UtilityPayload) => {
    return api.post<ApiResponse<unknown>>(URLS.callMeLater, requestData);
  },
};

export default utilityApi;
